/**
 * Multi-document / multi-PO product rules (Step 1).
 *
 * Three cases (do not conflate):
 *   (a) many_invoices_one_po  — several real invoices close one PO (partials)
 *   (b) many_pages_one_invoice — dual-photo / split scan of ONE document
 *   (c) one_invoice_many_pos — one invoice allocated across several POs
 *
 * Also locks:
 *   - duplicate vs merge (same invoice #)
 *   - Allocate vs multiple_matches (split £ vs pick one PO)
 *
 * Keep in sync with: express_app/utils/invoiceMultiDocRules.js
 *
 * Step 1–10: product rules through GR/Invoices multi-doc polish (pages vs
 * invoices, remaining £ ambient, Add invoice copy).
 */

export const INVOICE_MULTI_DOC_CASE = {
  MANY_INVOICES_ONE_PO: 'many_invoices_one_po',
  MANY_PAGES_ONE_INVOICE: 'many_pages_one_invoice',
  ONE_INVOICE_MANY_POS: 'one_invoice_many_pos',
}

export const INVOICE_MULTI_DOC_CASE_META = {
  [INVOICE_MULTI_DOC_CASE.MANY_INVOICES_ONE_PO]: {
    label: 'Many invoices → one PO',
    summary:
      'Several real invoices stack against one purchase order (partial deliveries). Attach ≠ close until remaining balance is covered.',
  },
  [INVOICE_MULTI_DOC_CASE.MANY_PAGES_ONE_INVOICE]: {
    label: 'Many pages → one invoice',
    summary:
      'Two photos or files of the same supplier invoice (same number). Merge pages into one Invoice row; Match once — not Possible duplicate first.',
  },
  [INVOICE_MULTI_DOC_CASE.ONE_INVOICE_MANY_POS]: {
    label: 'One invoice → many POs',
    summary:
      'One invoice document covers several purchase orders. Allocate slices; each PO soft-links with its slice only.',
  },
}

/** Outcome when an incoming upload collides with an existing invoice #. */
export const DUPLICATE_OR_MERGE_SIGNAL = {
  NONE: 'none',
  /** Dual-photo / add pages — prefer merge into existing open invoice. */
  MERGE_PAGES: 'merge_pages',
  /** True second invoice — keep Possible duplicate gate. */
  POSSIBLE_DUPLICATE: 'possible_duplicate',
}

/**
 * Next primary action when Matching is ambiguous or multi-PO.
 * multiple_matches alone → pick one PO (Match).
 * covers_multiple_orders / saved allocations → Allocate.
 */
export const MATCH_VS_ALLOCATE_ACTION = {
  NONE: 'none',
  MATCH_PICK_PO: 'match_pick_po',
  ALLOCATE_MULTI_PO: 'allocate_multi_po',
}

const CLOSED_LIFECYCLES = new Set(['reconciled', 'sentToXero'])
const OPEN_MERGE_LIFECYCLES = new Set([
  'pending',
  'attachedToPO',
  'error',
  'errorSendingToXero',
])

function normalise(value) {
  return String(value || '').trim()
}

/**
 * Same normalisation as express duplicate detection (conservative).
 */
export function normaliseInvoiceNumberForMultiDoc(value) {
  if (value == null) return ''
  return String(value)
    .trim()
    .toUpperCase()
    .replace(/[\s\-_]+/g, '')
    .replace(/[^A-Z0-9/#]/g, '')
}

function toPenniesOrNull(value) {
  if (value == null || value === '') return null
  const num = Number(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

function lifecycleOf(invoice = {}) {
  return normalise(invoice.status) || 'pending'
}

function hasSavedMultiPoAllocations(invoice = {}) {
  return (
    Array.isArray(invoice.poAllocations) && invoice.poAllocations.length >= 2
  )
}

function allocationPoKey(row = {}) {
  return normalise(row?.purchaseOrderNumber).toUpperCase()
}

/** Whether one allocation row has been Match & reconciled / Full-accepted. */
export function isPoAllocationReconciled(row = {}) {
  return Boolean(row?.reconciledAt)
}

export function hasReconciledPoAllocation(invoice = {}) {
  return (invoice.poAllocations || []).some(isPoAllocationReconciled)
}

export function listOpenPoAllocations(invoice = {}) {
  return (invoice.poAllocations || []).filter(
    row =>
      normalise(row?.purchaseOrderNumber) && !isPoAllocationReconciled(row),
  )
}

export function listReconciledPoAllocations(invoice = {}) {
  return (invoice.poAllocations || []).filter(isPoAllocationReconciled)
}

/**
 * True when every saved multi-PO slice is reconciled (or there are no rows).
 * Single-PO invoices without allocations are not "multi-PO complete" here.
 */
export function areAllPoAllocationsReconciled(invoice = {}) {
  if (!hasSavedMultiPoAllocations(invoice)) return false
  return (invoice.poAllocations || []).every(isPoAllocationReconciled)
}

/**
 * Pick which allocated PO Match & reconcile should close.
 * Prefers an explicit request, else the first open slice, else primary.
 */
export function resolveReconcileTargetPurchaseOrderNumber(
  invoice = {},
  requestedPurchaseOrderNumber = '',
) {
  const requested = normalise(requestedPurchaseOrderNumber)
  if (!hasSavedMultiPoAllocations(invoice)) {
    return (
      requested ||
      normalise(invoice.purchaseOrderNumber) ||
      normalise(invoice.matchSummary?.purchaseOrderNumber) ||
      ''
    )
  }

  const rows = invoice.poAllocations || []
  if (requested) {
    const match = rows.find(
      row => allocationPoKey(row) === requested.toUpperCase(),
    )
    if (match) return normalise(match.purchaseOrderNumber)
  }

  const open = listOpenPoAllocations(invoice)
  if (open.length) return normalise(open[0].purchaseOrderNumber)

  return normalise(rows[0]?.purchaseOrderNumber)
}

/**
 * Mark one allocation slice reconciled. Returns whether the invoice is fully done.
 * Does not mutate invoice lifecycle — caller stamps attached vs reconciled.
 */
export function markPoAllocationReconciled(
  invoice = {},
  purchaseOrderNumber = '',
  actor = {},
) {
  const target = normalise(purchaseOrderNumber).toUpperCase()
  if (!target || !Array.isArray(invoice.poAllocations)) {
    return {
      marked: false,
      invoiceFullyReconciled: false,
      openPurchaseOrderNumbers: [],
    }
  }

  const now = new Date()
  const next = invoice.poAllocations.map(row => {
    const plain =
      row && typeof row.toObject === 'function' ? row.toObject() : { ...row }
    if (allocationPoKey(plain) !== target) return plain
    if (plain.reconciledAt) return plain
    return {
      ...plain,
      reconciledAt: now,
      reconciledBy: actor.accountId || undefined,
      reconciledByName: actor.name || actor.username || '',
    }
  })

  invoice.poAllocations = next

  const openPurchaseOrderNumbers = listOpenPoAllocations(invoice).map(row =>
    normalise(row.purchaseOrderNumber),
  )

  return {
    marked: true,
    invoiceFullyReconciled: openPurchaseOrderNumbers.length === 0,
    openPurchaseOrderNumbers,
    reconciledPurchaseOrderNumber: normalise(purchaseOrderNumber),
  }
}

/**
 * Product rule: `multiple_matches` means “pick the right PO”, not Allocate.
 * Allocate is for true multi-PO coverage (failure reason, suggest, or saved splits).
 */
export function isMultipleMatchesAmbiguityOnly(invoice = {}) {
  if (normalise(invoice.matchingStatus) !== 'multiple_matches') return false
  if (normalise(invoice.matchingFailureReason) === 'covers_multiple_orders') {
    return false
  }
  if (hasSavedMultiPoAllocations(invoice)) return false
  return true
}

/**
 * Whether Allocate is the intended primary path (row CTA + health).
 * Intentionally does NOT treat bare `multiple_matches` as Allocate.
 */
export function shouldOfferAllocateMultiPo(invoice = {}) {
  const lifecycle = lifecycleOf(invoice)
  if (CLOSED_LIFECYCLES.has(lifecycle)) return false
  if (invoice.isArchived) return false
  if (hasReconciledPoAllocation(invoice)) return false

  if (hasSavedMultiPoAllocations(invoice)) return true
  if (normalise(invoice.matchingFailureReason) === 'covers_multiple_orders') {
    return true
  }
  // Engine will set this when Step 5 lands; honour if already present.
  if (normalise(invoice.matchingStatus) === 'covers_multiple_orders') {
    return true
  }
  return false
}

/**
 * Classify Match-vs-Allocate for an invoice row (pure product rule).
 */
export function classifyMatchVsAllocateAction(invoice = {}) {
  if (shouldOfferAllocateMultiPo(invoice)) {
    return MATCH_VS_ALLOCATE_ACTION.ALLOCATE_MULTI_PO
  }
  if (isMultipleMatchesAmbiguityOnly(invoice)) {
    return MATCH_VS_ALLOCATE_ACTION.MATCH_PICK_PO
  }
  if (normalise(invoice.matchingStatus) === 'multiple_matches') {
    // multiple_matches + something else already handled above; leftover
    return MATCH_VS_ALLOCATE_ACTION.MATCH_PICK_PO
  }
  return MATCH_VS_ALLOCATE_ACTION.NONE
}

/**
 * Suggest Allocate when invoice total ≈ sum of 2+ open PO candidate totals.
 * Pure math — used by the engine and UI.
 *
 * @param {{
 *   invoiceTotalPennies?: number|null,
 *   candidatePoTotalsPennies?: number[],
 *   tolerancePennies?: number,
 * }} input
 */
export function shouldSuggestAllocateFromCandidateTotals({
  invoiceTotalPennies = null,
  candidatePoTotalsPennies = [],
  tolerancePennies = 0,
} = {}) {
  const invoiceTotal = toPenniesOrNull(invoiceTotalPennies)
  if (invoiceTotal == null || invoiceTotal <= 0) return false

  const totals = (candidatePoTotalsPennies || [])
    .map(toPenniesOrNull)
    .filter(v => v != null && v > 0)
  if (totals.length < 2) return false

  const sum = totals.reduce((acc, n) => acc + n, 0)
  const tolerance = Math.max(0, toPenniesOrNull(tolerancePennies) || 0)
  return Math.abs(sum - invoiceTotal) <= tolerance
}

/**
 * Pick a set of 2+ open, distinct-PO candidates whose open contributions sum
 * to the invoice total (within tolerance). Prefers smaller sets and sets that
 * include the invoice's entered PO when present.
 *
 * @param {{
 *   candidates?: Array<{
 *     purchaseOrderNumber?: string,
 *     contributionPennies?: number|null,
 *     orderGrossPennies?: number|null,
 *     validForReconciliation?: boolean,
 *     isReconciled?: boolean,
 *   }>,
 *   invoiceTotalPennies?: number|null,
 *   tolerancePennies?: number,
 *   preferredPurchaseOrderNumber?: string|null,
 *   maxSetSize?: number,
 * }} input
 * @returns {null | { candidates: object[], totalsPennies: number[], purchaseOrderNumbers: string[] }}
 */
export function findAllocateSuggestCandidateSet({
  candidates = [],
  invoiceTotalPennies = null,
  tolerancePennies = 0,
  preferredPurchaseOrderNumber = null,
  maxSetSize = 5,
} = {}) {
  const invoiceTotal = toPenniesOrNull(invoiceTotalPennies)
  if (invoiceTotal == null || invoiceTotal <= 0) return null

  const normalisePoKey = value =>
    String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[\s\-_]+/g, '')

  const preferredPoKey = normalisePoKey(preferredPurchaseOrderNumber)

  const byPo = new Map()
  for (const candidate of candidates || []) {
    if (!candidate || candidate.isReconciled) continue
    if (candidate.validForReconciliation === false) continue

    const po = String(candidate.purchaseOrderNumber || '').trim()
    const poKey = normalisePoKey(po)
    if (!poKey) continue

    const contribution =
      toPenniesOrNull(candidate.contributionPennies) ??
      toPenniesOrNull(candidate.orderGrossPennies)
    if (contribution == null || contribution <= 0) continue

    const existing = byPo.get(poKey)
    if (!existing || contribution > existing.contributionPennies) {
      byPo.set(poKey, {
        candidate,
        purchaseOrderNumber: po,
        contributionPennies: contribution,
        isPreferred: Boolean(preferredPoKey) && preferredPoKey === poKey,
      })
    }
  }

  const unique = [...byPo.values()]
  if (unique.length < 2) return null

  const cap = Math.max(2, Math.min(Number(maxSetSize) || 5, unique.length, 6))
  const tolerance = Math.max(0, toPenniesOrNull(tolerancePennies) || 0)

  let best = null
  for (let size = 2; size <= cap; size += 1) {
    const choose = (start, chosen) => {
      if (best && best.candidates.length < size) return
      if (chosen.length === size) {
        const totalsPennies = chosen.map(row => row.contributionPennies)
        if (
          !shouldSuggestAllocateFromCandidateTotals({
            invoiceTotalPennies: invoiceTotal,
            candidatePoTotalsPennies: totalsPennies,
            tolerancePennies: tolerance,
          })
        ) {
          return
        }
        const includesPreferred = chosen.some(row => row.isPreferred)
        const next = {
          candidates: chosen.map(row => row.candidate),
          totalsPennies,
          purchaseOrderNumbers: chosen.map(row => row.purchaseOrderNumber),
          includesPreferred,
        }
        if (
          !best ||
          next.candidates.length < best.candidates.length ||
          (next.candidates.length === best.candidates.length &&
            next.includesPreferred &&
            !best.includesPreferred)
        ) {
          best = next
        }
        return
      }
      for (let i = start; i < unique.length; i += 1) {
        choose(i + 1, [...chosen, unique[i]])
      }
    }
    choose(0, [])
    if (best) break
  }

  if (!best) return null
  return {
    candidates: best.candidates,
    totalsPennies: best.totalsPennies,
    purchaseOrderNumbers: best.purchaseOrderNumbers,
  }
}

/**
 * Same invoice # + same venue + same supplier is required for merge OR duplicate.
 * Then:
 *   - existing still open + totals compatible → MERGE_PAGES (dual-photo)
 *   - existing closed (reconciled / Xero) → POSSIBLE_DUPLICATE
 *   - totals differ beyond tolerance → POSSIBLE_DUPLICATE
 *   - otherwise → POSSIBLE_DUPLICATE (safe default until Step 2 UX)
 *
 * @param {{
 *   incoming?: object,
 *   existing?: object,
 *   tolerancePennies?: number,
 * }} input
 */
export function classifyDuplicateOrMergeSignal({
  incoming = {},
  existing = {},
  tolerancePennies = 0,
} = {}) {
  const incomingNumber = normaliseInvoiceNumberForMultiDoc(
    incoming.invoiceNumber,
  )
  const existingNumber = normaliseInvoiceNumberForMultiDoc(
    existing.invoiceNumber,
  )
  if (!incomingNumber || !existingNumber || incomingNumber !== existingNumber) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.NONE,
      reason: 'invoice_number_mismatch',
    }
  }

  const incomingVenue = normalise(incoming.venueId || incoming.venue)
  const existingVenue = normalise(
    existing.venueId || existing.venue?._id || existing.venue,
  )
  if (!incomingVenue || !existingVenue || incomingVenue !== existingVenue) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.NONE,
      reason: 'venue_mismatch',
    }
  }

  const incomingSupplier = normalise(
    incoming.venueSupplierId || incoming.supplierId || incoming.supplier,
  )
  const existingSupplier = normalise(
    existing.venueSupplierId ||
      existing.supplierId ||
      existing.supplier?._id ||
      existing.supplier,
  )
  // Soft: if both sides have a supplier id, they must match.
  if (
    incomingSupplier &&
    existingSupplier &&
    incomingSupplier !== existingSupplier
  ) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.NONE,
      reason: 'supplier_mismatch',
    }
  }

  const existingLifecycle = lifecycleOf(existing)
  if (CLOSED_LIFECYCLES.has(existingLifecycle)) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.POSSIBLE_DUPLICATE,
      reason: 'existing_already_closed',
    }
  }

  const incomingTotal = toPenniesOrNull(
    incoming.invoiceTotal ?? incoming.invoiceTotalPennies,
  )
  const existingTotal = toPenniesOrNull(
    existing.invoiceTotal ?? existing.invoiceTotalPennies,
  )
  const tolerance = Math.max(0, toPenniesOrNull(tolerancePennies) || 0)

  if (
    incomingTotal != null &&
    existingTotal != null &&
    Math.abs(incomingTotal - existingTotal) > tolerance
  ) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.POSSIBLE_DUPLICATE,
      reason: 'totals_differ',
    }
  }

  if (OPEN_MERGE_LIFECYCLES.has(existingLifecycle) || !existing.status) {
    return {
      signal: DUPLICATE_OR_MERGE_SIGNAL.MERGE_PAGES,
      reason: 'same_number_open_existing',
    }
  }

  return {
    signal: DUPLICATE_OR_MERGE_SIGNAL.POSSIBLE_DUPLICATE,
    reason: 'default_duplicate',
  }
}

/**
 * Classify which multi-doc case a situation represents (for docs/tests/later UI).
 *
 * Precedence:
 *   1. Saved multi-PO allocations → one_invoice_many_pos
 *   2. Explicit merge / multi-page on one invoice → many_pages_one_invoice
 *   3. 2+ distinct invoices on one PO → many_invoices_one_po
 */
export function classifyInvoiceMultiDocCase({
  invoicesOnPo = [],
  pageCount = 1,
  allocations = [],
  mergeSignal = null,
} = {}) {
  const allocationRows = Array.isArray(allocations) ? allocations : []
  if (allocationRows.length >= 2) {
    return INVOICE_MULTI_DOC_CASE.ONE_INVOICE_MANY_POS
  }

  if (mergeSignal === DUPLICATE_OR_MERGE_SIGNAL.MERGE_PAGES || pageCount > 1) {
    return INVOICE_MULTI_DOC_CASE.MANY_PAGES_ONE_INVOICE
  }

  const distinct = (invoicesOnPo || []).filter(Boolean)
  if (distinct.length >= 2) {
    return INVOICE_MULTI_DOC_CASE.MANY_INVOICES_ONE_PO
  }

  return null
}

/**
 * After findPossibleDuplicateInvoices: split peers into merge-pages vs true
 * duplicate using product rules. Merge-only → do not gate as possible_duplicate.
 *
 * @param {object} duplicateResult from findPossibleDuplicateInvoices
 * @param {object} incoming invoice-like fields
 * @param {number} [tolerancePennies]
 */
export function refineDuplicateResultWithMergeRules(
  duplicateResult = null,
  incoming = {},
  tolerancePennies = 0,
) {
  if (!duplicateResult?.isDuplicate) {
    return {
      duplicateResult: duplicateResult || {
        isDuplicate: false,
        matches: [],
        confidence: null,
      },
      addPagesOffer: null,
    }
  }

  const matches = Array.isArray(duplicateResult.matches)
    ? duplicateResult.matches
    : []

  const classified = matches.map(match => {
    const { signal, reason } = classifyDuplicateOrMergeSignal({
      incoming,
      existing: {
        invoiceNumber: match.invoiceNumber,
        venueId: incoming.venueId || incoming.venue,
        venueSupplierId:
          incoming.venueSupplierId || incoming.supplierId || incoming.supplier,
        supplier: match.supplier,
        status: match.status,
        invoiceTotal: match.invoiceTotal,
        invoiceTotalPennies: match.invoiceTotal,
      },
      tolerancePennies,
    })
    return { ...match, mergeSignal: signal, mergeReason: reason }
  })

  const mergeMatches = classified.filter(
    match => match.mergeSignal === DUPLICATE_OR_MERGE_SIGNAL.MERGE_PAGES,
  )
  const duplicateMatches = classified.filter(
    match => match.mergeSignal === DUPLICATE_OR_MERGE_SIGNAL.POSSIBLE_DUPLICATE,
  )

  // Any true-duplicate peer wins — keep Possible duplicate gate.
  if (duplicateMatches.length > 0) {
    const rank = { exact: 3, high: 2, possible: 1 }
    const sorted = [...duplicateMatches].sort(
      (a, b) => (rank[b.confidence] || 0) - (rank[a.confidence] || 0),
    )
    return {
      duplicateResult: {
        isDuplicate: true,
        confidence: sorted[0]?.confidence || duplicateResult.confidence,
        matches: sorted,
        reason: sorted[0]?.reason || duplicateResult.reason,
      },
      addPagesOffer: null,
    }
  }

  if (mergeMatches.length > 0) {
    const rank = { exact: 3, high: 2, possible: 1 }
    const sorted = [...mergeMatches].sort(
      (a, b) => (rank[b.confidence] || 0) - (rank[a.confidence] || 0),
    )
    const primary = sorted[0]
    return {
      duplicateResult: {
        isDuplicate: false,
        matches: [],
        confidence: null,
        reason: null,
      },
      addPagesOffer: {
        offered: true,
        targetInvoiceId: String(primary.invoiceId || '').trim(),
        targetInvoiceNumber: primary.invoiceNumber || '',
        reason: primary.mergeReason || 'same_number_open_existing',
        confidence: primary.confidence || null,
        matchInvoiceIds: sorted
          .map(match => String(match.invoiceId || '').trim())
          .filter(Boolean)
          .slice(0, 10),
      },
    }
  }

  return {
    duplicateResult,
    addPagesOffer: null,
  }
}

/**
 * Slim matchSummary fields for an Add-pages offer (Step 2 detect).
 */
export function buildAddPagesMatchSummaryFields(addPagesOffer = null) {
  if (!addPagesOffer?.offered || !addPagesOffer.targetInvoiceId) {
    return {
      addPagesOffered: false,
      addPagesTargetInvoiceId: null,
      addPagesTargetInvoiceNumber: null,
      addPagesReason: null,
      addPagesConfidence: null,
      addPagesMatchInvoiceIds: [],
    }
  }

  return {
    addPagesOffered: true,
    addPagesTargetInvoiceId: String(addPagesOffer.targetInvoiceId),
    addPagesTargetInvoiceNumber: addPagesOffer.targetInvoiceNumber || null,
    addPagesReason: addPagesOffer.reason || null,
    addPagesConfidence: addPagesOffer.confidence || null,
    addPagesMatchInvoiceIds: Array.isArray(addPagesOffer.matchInvoiceIds)
      ? addPagesOffer.matchInvoiceIds.map(id => String(id))
      : [],
  }
}

/**
 * After allocate: each PO contribution should be the slice, not the full total.
 * Used by remaining-balance / smart-match contribution (Step 6).
 */
export function getAllocatedContributionPenniesForPo(
  invoice = {},
  purchaseOrderNumber = '',
) {
  const po = normalise(purchaseOrderNumber).toUpperCase()
  if (!po || !Array.isArray(invoice.poAllocations)) return null

  const row = invoice.poAllocations.find(
    allocation =>
      normalise(allocation?.purchaseOrderNumber).toUpperCase() === po,
  )
  if (!row) return null

  const pennies = toPenniesOrNull(row.amountPennies)
  return pennies == null ? null : pennies
}

function formatSlicePounds(pennies) {
  const value = toPenniesOrNull(pennies)
  if (value == null) return '—'
  return `£${(Math.abs(value) / 100).toFixed(2)}`
}

/**
 * Display summary for multi-PO allocations (Invoices Status + GR labels).
 */
export function getMultiPoAllocationSummary(invoice = {}) {
  const rows = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations.filter(
        row =>
          normalise(row?.purchaseOrderNumber) &&
          toPenniesOrNull(row?.amountPennies) != null,
      )
    : []

  if (rows.length < 2) {
    return {
      isMultiPo: false,
      count: rows.length,
      label: '',
      subtitle: '',
      slices: [],
    }
  }

  const slices = rows.map(row => {
    const purchaseOrderNumber = normalise(row.purchaseOrderNumber)
    const amountPennies = toPenniesOrNull(row.amountPennies)
    const reconciled = isPoAllocationReconciled(row)
    const amountLabel = formatSlicePounds(amountPennies)
    return {
      purchaseOrderNumber,
      amountPennies,
      reconciled,
      label: reconciled
        ? `${purchaseOrderNumber} · ${amountLabel} (done)`
        : `${purchaseOrderNumber} · ${amountLabel}`,
    }
  })

  const openCount = slices.filter(slice => !slice.reconciled).length

  return {
    isMultiPo: true,
    count: slices.length,
    openCount,
    label: `Allocated across ${slices.length} POs`,
    subtitle: slices.map(slice => slice.label).join(' · '),
    slices,
  }
}

/**
 * Per-PO slice label for GR Invoice column (e.g. "£100.00").
 */
export function getAllocationSliceDisplayForPo(
  invoice = {},
  purchaseOrderNumber = '',
) {
  const slice = getAllocatedContributionPenniesForPo(
    invoice,
    purchaseOrderNumber,
  )
  if (slice == null) return ''
  return formatSlicePounds(slice)
}

/**
 * Allocations may be edited until any allocated PO slice is reconciled / Xero.
 */
export function canEditInvoicePoAllocations(invoice = {}) {
  if (invoice.isArchived) return false
  const lifecycle = lifecycleOf(invoice)
  if (CLOSED_LIFECYCLES.has(lifecycle)) return false
  if (hasReconciledPoAllocation(invoice)) return false
  if (!hasSavedMultiPoAllocations(invoice)) {
    return !CLOSED_LIFECYCLES.has(lifecycle)
  }
  return true
}

/** True when enrich/match stored an Add-pages proposal (Step 2). */
export function hasAddPagesOffer(invoice = {}) {
  return Boolean(
    invoice?.matchSummary?.addPagesOffered &&
      invoice?.matchSummary?.addPagesTargetInvoiceId,
  )
}

export function getAddPagesOfferMeta(invoice = {}) {
  if (!hasAddPagesOffer(invoice)) {
    return {
      offered: false,
      buttonLabel: 'Add to existing invoice',
      badgeLabel: '',
      targetInvoiceId: '',
      targetInvoiceNumber: '',
      guidance: '',
    }
  }

  const number = normalise(invoice.matchSummary.addPagesTargetInvoiceNumber)
  const targetLabel = number ? `#${number}` : 'the existing invoice'

  return {
    offered: true,
    buttonLabel: 'Add to existing invoice',
    badgeLabel: 'Add to existing?',
    targetInvoiceId: String(invoice.matchSummary.addPagesTargetInvoiceId),
    targetInvoiceNumber: number,
    guidance: `This looks like another photo of ${targetLabel} (same invoice number). Add these pages to the existing invoice so you Match and reconcile once.`,
  }
}

/**
 * Build an additionalPages entry from a stub invoice about to be merged away.
 */
export function buildAdditionalPageFromStubInvoice(stub = {}) {
  const key = normalise(stub.key)
  if (!key) return null

  return {
    key,
    invoiceUrl: stub.invoiceUrl || '',
    originalFilename: stub.originalFilename || '',
    uploadedDate: stub.uploadedDate || new Date(),
    sourceInvoiceId: stub._id || stub.id || null,
    contentType: stub.contentType || '',
  }
}

/**
 * Primary file + additionalPages as a flat list for preview UI.
 */
export function listInvoiceDocumentPages(invoice = {}) {
  const pages = []
  const primaryKey = normalise(invoice.key)
  if (primaryKey) {
    pages.push({
      key: primaryKey,
      invoiceUrl: invoice.invoiceUrl || '',
      originalFilename: invoice.originalFilename || '',
      uploadedDate: invoice.uploadedDate || null,
      isPrimary: true,
      sourceInvoiceId: null,
    })
  }

  for (const page of invoice.additionalPages || []) {
    const key = normalise(page?.key)
    if (!key) continue
    pages.push({
      key,
      invoiceUrl: page.invoiceUrl || '',
      originalFilename: page.originalFilename || '',
      uploadedDate: page.uploadedDate || null,
      isPrimary: false,
      sourceInvoiceId: page.sourceInvoiceId
        ? String(page.sourceInvoiceId)
        : null,
    })
  }

  return pages
}

export function getInvoicePageCount(invoice = {}) {
  return Math.max(1, listInvoiceDocumentPages(invoice).length)
}
